/**
 * ChatUploadZone — inline document upload widget for WorkspaceChatPanel.
 *
 * Renders an assistant bubble prompting the user to upload a specific
 * document type, followed by a drag-and-drop / click-to-browse drop zone.
 *
 * Pure UI — the parent page owns the upload mutation and passes `uploading`,
 * `error`, `onFile`, and `onSkip` as props. No API calls here.
 */
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

// ─── Shared types ─────────────────────────────────────────────────────────────

/** Describes a document that has just been uploaded and awaits extraction review. */
export interface PendingExtraction {
  documentId: string;
  documentType: string;
  fileName: string;
}

/** A document whose extracted fields the user has confirmed (session-only). */
export interface ConfirmedExtraction {
  documentId: string;
  documentType: string;
  fileName: string;
  fields: { label: string; value: string }[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const DOC_TYPE_LABELS: Record<string, string> = {
  green_card: "Permanent Resident Card (Green Card)",
  passport_photo: "Passport-style photo",
  marriage_certificate: "Marriage certificate",
  divorce_decree: "Divorce decree",
  name_change: "Name change document",
};

/** Mock extracted fields per document type (frontend-only in Phase 1). */
export const MOCK_FIELDS: Record<string, { label: string; value: string }[]> = {
  green_card: [
    { label: "A-Number", value: "A-123 456 789" },
    { label: "Date became permanent resident", value: "January 15, 2017" },
    { label: "Card expiry", value: "May 2029" },
  ],
  passport_photo: [
    { label: "Full name", value: "John A. Smith" },
    { label: "Date of birth", value: "March 3, 1985" },
  ],
  marriage_certificate: [
    { label: "Spouse full name", value: "Jane Smith" },
    { label: "Marriage date", value: "June 12, 2018" },
    { label: "Marriage location", value: "New York, NY" },
  ],
  divorce_decree: [
    { label: "Former spouse name", value: "Jane Smith" },
    { label: "Divorce date", value: "April 5, 2015" },
  ],
  name_change: [
    { label: "Former name", value: "John Doe" },
    { label: "New name", value: "John Smith" },
    { label: "Date of change", value: "August 1, 2020" },
  ],
};

const DOC_TYPE_PROMPTS: Record<string, string> = {
  green_card:
    "Please upload both sides of your Permanent Resident Card. A clear photo or scan works fine.",
  passport_photo:
    "Please upload your passport-style photo — face visible, light background.",
  marriage_certificate: "Please upload a copy of your marriage certificate.",
  divorce_decree: "Please upload a copy of your divorce decree.",
  name_change: "Please upload documentation of your legal name change.",
};

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  documentType: string;
  uploading: boolean;
  error: string | null;
  onFile: (file: File) => void;
  onSkip: () => void;
}

export function ChatUploadZone({ documentType, uploading, error, onFile, onSkip }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const label = DOC_TYPE_LABELS[documentType] ?? documentType;
  const prompt = DOC_TYPE_PROMPTS[documentType] ?? `Please upload your ${label}.`;

  function pick(file: File) {
    if (file.size > MAX_FILE_BYTES) {
      alert("File must be under 10 MB. Please compress or resize, then try again.");
      return;
    }
    onFile(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && !uploading) pick(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) pick(file);
    e.target.value = "";
  }

  return (
    <div className="space-y-3">
      {/* Assistant prompt bubble */}
      <div className="flex gap-2.5">
        <AssistantIcon />
        <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
          <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-1">{label}</p>
          <p className="text-sm text-slate-700 leading-relaxed">{prompt}</p>
        </div>
      </div>

      {/* Drop zone / upload state */}
      <div className="pl-9">
        {uploading ? (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0" />
            <p className="text-sm text-blue-700 font-medium">Uploading…</p>
          </div>
        ) : (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={cn(
              "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-colors select-none",
              dragging
                ? "border-blue-400 bg-blue-50"
                : "border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/40",
            )}
          >
            <svg
              className={cn("w-7 h-7 transition-colors", dragging ? "text-blue-400" : "text-slate-300")}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <div className="text-center">
              <p className="text-sm text-slate-500">
                <span className="font-semibold text-blue-600">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-slate-400 mt-0.5">JPG, PNG or PDF · max 10 MB</p>
            </div>
          </div>
        )}

        {error && !uploading && (
          <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 leading-snug">
            {error}
          </p>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleChange}
          className="hidden"
        />

        {!uploading && (
          <button
            onClick={onSkip}
            className="mt-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Skip for now →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Icon helper ─────────────────────────────────────────────────────────────

function AssistantIcon() {
  return (
    <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
      </svg>
    </div>
  );
}
